import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, Plus } from 'lucide-react';
import Icon from '../../../Icon';
import { Banner } from '../shared';
import { useAdvMeta } from './shared';
import AdvDashboard from './Dashboard';
import AdvForm from './Form';
import AdvRegister from './Register';
import AdvStatement from './Statement';
import AdvSettings from './Settings';
import AdvAudit from './Audit';
import Loader from '../../../components/Loader';

// HR Request Center > Advanced (Stephanet's custom/HRAdvancedRequests.jsx): Advanced Requisition and Request Management with its own
// left navigation — dashboard, new request, 17 category registers, my / pending / assigned / completed / rejected-returned, statement,
// reports, settings and audit trail. The view is the address's ?v=.

export default function AdvancedRequests() {
  const { meta, error, reload } = useAdvMeta();
  const [params, setParams] = useSearchParams();
  const view = params.get('v') || 'dashboard';
  const [showNav, setShowNav] = useState(true);
  const [editing, setEditing] = useState(null);   // {id} or {} for a new request
  const [status, setStatus] = useState(null);     // a dashboard card → the All register's status
  const go = (v, st = null) => {
    setEditing(null); setStatus(st);
    setParams(v === 'dashboard' ? {} : { v });
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  if (error && !meta) return <div className="error">{error}</div>;
  if (!meta) return <Loader title="Loading advanced requests…" />;
  const nav = meta.nav.find(n => n.key === view) || meta.nav[0];
  const saved = () => { setEditing(null); go('my_requests'); };
  const onEdit = id => setEditing({ id });

  let body;
  if (editing) body = <AdvForm meta={meta} id={editing.id} onSaved={saved} onCancel={() => setEditing(null)} />;
  else if (view === 'dashboard') body = <AdvDashboard meta={meta} onFilter={st => go('all', st)} />;
  else if (view === 'new') body = <AdvForm meta={meta} onSaved={saved} onCancel={() => go('dashboard')} />;
  else if (view === 'statement' || view === 'reports') body = <AdvStatement key={view} meta={meta} mode={view} />;
  else if (view === 'settings') body = <AdvSettings meta={meta} reloadMeta={reload} />;
  else if (view === 'audit_trail') body = <AdvAudit />;
  else if (view === 'rejected_returned') {
    body = (
      <div className="adv-split">
        <AdvRegister meta={meta} title="Rejected Requests" filter={{ status: 'Rejected' }} onEdit={onEdit} />
        <AdvRegister meta={meta} title="Returned for Correction" filter={{ status: 'Returned for Correction' }} onEdit={onEdit} />
      </div>
    );
  } else {
    const filter = view === 'all' ? { status } : { ...(nav.filter || {}) };
    body = <AdvRegister key={`${view}${status}`} meta={meta} title={view === 'all' ? (status ? `${status} Requests` : 'All Requests') : nav.label}
                        filter={filter} onEdit={onEdit} />;
  }

  return (
    <div className="mdr mds hrc adv">
      <Banner title="Advanced Requisition and Request Management"
              text="Universal request system — cost requests, non-cost requests, procurement, payment, HR, fleet, IT, security, HSE, legal, admin, finance, petty cash, reimbursement, and all company requests">
        {meta.can.create && <button className="mdr-btn att2-mark" onClick={() => setEditing({})}><Plus size={14} /> New Advanced Request</button>}
        <button className="mdr-btn" onClick={() => setShowNav(s => !s)}>{showNav ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />} {showNav ? 'Hide Nav' : 'Show Nav'}</button>
      </Banner>
      <div className={`adv-layout ${showNav ? '' : 'nonav'}`}>
        {showNav && (
          <nav className="adv-nav no-print">
            {meta.nav.map(n => (
              <button key={n.key} className={!editing && view === n.key ? 'active' : ''} onClick={() => (n.key === 'new' ? setEditing({}) : go(n.key))}>
                <Icon name={n.icon} size={13} /> <span>{n.label}</span>
              </button>
            ))}
          </nav>
        )}
        <div className="adv-body">{body}</div>
      </div>
    </div>
  );
}
